/**
 * DiagnosticoCard — o laudo do @DevOps, renderizado no chat (TASK-10426).
 *
 * **Este cartão é o chão de fatos.** A prosa do @Atendente fica ACIMA dele e
 * nunca o substitui: se o modelo enfeitar, o usuário ainda vê o que foi de fato
 * verificado — e, principalmente, o que NÃO foi.
 *
 * Três decisões que vêm do desenho, não de gosto:
 *
 * 1. **A seção "o que não verifiquei" é visível**, nunca escondida atrás de
 *    "detalhes". Ausência de leitura é resultado de primeira classe: é ela que
 *    separa "li e está errado" de "não consegui ler", e é o que impede o laudo
 *    de virar diagnóstico de saúde sobre dado que ninguém leu.
 * 2. **`sem_falha_encontrada` tem cara de resposta afirmativa.** A pergunta
 *    "está funcionando?" merece "está", não "não cheguei a uma causa".
 * 3. **`suspeito` é um estado próprio**, distinto de ok e de falha: "1 de 2
 *    alvos respondem" não é saúde nem é a causa de o domínio não abrir.
 *
 * O laudo chega pelo `metadata.diagnostico` da mensagem do bot — mesmo trilho do
 * `proposed_action`. O que vem aqui já foi serializado no servidor: identificador
 * (ARN, id de conta, IP interno) só aparece para quem tem a role devops.
 *
 * 4. **"Isso não bate" fica no cartão** (TASK-10428), e não num formulário de
 *    feedback à parte. Ler ao vivo não impede concluir errado: a causa provável
 *    sai de REGRAS escritas por nós. Em 14/09/2026 um reboot em produção foi
 *    proposto sobre a premissa "o SSM não responde" — o SSM respondia. Quem
 *    estava na conversa sabia, e não havia onde dizer. O sinal precisa ficar
 *    onde a afirmação está, ou não é usado.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Code,
  Button,
  Card,
  Group,
  Popover,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconBulb,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconEyeOff,
  IconHelpCircle,
  IconInfoCircle,
  IconRecycle,
  IconThumbDown,
  IconX,
} from '@tabler/icons-react'

import { useDiscordarDoLaudo } from '../../../hooks/useChat'
import { FERRAMENTAS_COM_ROTULO, friendlyToolLabel } from '../toolLabels'
import { AlternativasDoLaudo, type Alternativa, type RegraDoLaudo } from './AlternativasDoLaudo'
import { useAuthStore } from '../../../store/auth'
import { useAplicarNoEditor } from '../../../store/aplicarNoEditor'

// ── Tipos ──────────────────────────────────────────────────────────────────

export type Veredito = 'ok' | 'falha' | 'suspeito' | 'nao_verificavel'

export interface ChecagemLaudo {
  id: string
  titulo: string
  veredito: Veredito
  observado: string
  motivo_nao_verificavel?: string | null
  detalhe_interno?: Record<string, unknown>
}

export interface LaudoDiagnostico {
  diagnostico: string
  status: 'concluido' | 'sem_falha_encontrada' | 'inconclusivo' | 'parcial'
  causa_provavel?: { id: string; resumo_publico: string } | null
  resumo_para_usuario: string
  checagens: ChecagemLaudo[]
  nao_verificado?: string[]
  precisa_do_usuario?: { campo: string; pergunta: string }[]
  // O que a receita NÃO alcança, declarado pelo backend (TASK-10851). Chega
  // como campo de TOPO do laudo, então atravessa `redaction.serializar`
  // intacto — e é uma string FIXA lá justamente por isso. O cartão a
  // renderiza verbatim: montar o texto aqui reintroduziria o endereçamento
  // que o backend evitou.
  escopo_da_verificacao?: string | null
  // O que fazer com o disco cheio (TASK-10441). Campo de TOPO, como
  // `escopo_da_verificacao`, e pelo mesmo motivo: atravessa
  // `redaction.serializar` intacto, então o backend só põe aqui slug, nome do
  // catálogo, caminho de sistema de arquivos e KB — nunca endereçamento.
  //
  // `tipo` é o que decide o que a tela diz, e os dois casos são resultado:
  // `acao_proposta` = medi, achei e existe limpeza liberada; `nenhuma` = medi e
  // NÃO há o que propor, com o `motivo` dizendo por quê. O `motivo` é texto do
  // backend, renderizado verbatim — montá-lo aqui duplicaria a regra e deixaria
  // a tela afirmando o que o servidor não afirmou.
  proxima_acao_sugerida?: {
    tipo: 'acao_proposta' | 'nenhuma'
    caminho: string
    kb: number
    total_kb: number
    verbo?: string
    nome?: string | null
    motivo?: string
  } | null
  // As alternativas do catálogo para a montagem do alerta (spec 2026-09-27 §5).
  // Campo de TOPO: atravessa `redaction.serializar` intacto. O `motivo` de
  // "sem alternativa" é texto do backend, renderizado verbatim.
  alternativas?: { alternativas: Alternativa[]; motivo_sem_alternativa: string | null } | null
  montagem_do_alerta?: { montagem: string; uso_pct?: number | null; tamanho_bytes?: number | null } | null
  // A leitura do CONJUNTO (Fase 1). Distinta de `causa_provavel`, que sai das
  // regras determinísticas: a hipótese é um andar acima, nunca por cima.
  hipotese?: { resumo: string; ancoras: string[]; confianca: 'alta' | 'media' | 'baixa' } | null
  // O ALVO efetivamente lido, e de onde ele veio (TASK-11810, Fatia 0 — âncora
  // no alerta). `resolvido_por` decide a frase de procedência: `ancora` (o IP
  // do alerta que abriu a conversa venceu), `chamado` (o resource_id do
  // chamado desta conversa), `texto_do_usuario` (o humano
  // escreveu o alvo) ou `nome` (resolvido pelo nome, sem âncora nem texto).
  // `detalhe_interno` só chega para quem tem a role que enxerga identificador
  // de infraestrutura (D19) — o backend já STRIPA para os demais, então o
  // cartão apenas renderiza quando presente, nunca deriva.
  alvo_exibido?: {
    nome: string
    tenant_nome: string
    resolvido_por: 'ancora' | 'chamado' | 'texto_do_usuario' | 'nome' | 'consulta_do_editor' | 'banco_da_conversa'
    detalhe_interno?: { instance_id: string; regiao?: string | null }
    // O alvo lido NÃO é a máquina do alerta (o humano escreveu outra): o
    // backend manda o NOME público da máquina do alerta, nunca o id (R-F3).
    diverge_do_alerta?: { maquina_do_alerta: string } | null
  } | null
  // A CONTRADIÇÃO entre o que o alerta diz e o que a leitura ao vivo achou
  // (TASK-11810). É AVISO sobre o alvo, não veredito de checagem — por isso
  // fica de fora de `checagens` e não ganha `data-veredito`: um ok/falha aqui
  // leria como resultado da checagem, quando na verdade é "o alerta e a
  // leitura não batem, confira se é a máquina certa".
  contradicao_com_alerta?: {
    dimensao: string
    montagem: string
    alerta_pct: number
    lido_pct: number | null
    motivo: 'leitura_abaixo_do_alerta' | 'montagem_ausente'
  } | null
  // A INVESTIGAÇÃO LIVRE do @DevOps (Fase 3, `assistant_tools.py` §4.1/§4.6).
  // Duas formas: rodou (`id`/`encerrada_por`/`leituras`, gravada em
  // `devops_investigacoes`) ou foi barrada pelo portão ANTES de começar
  // (`recusada`/`mensagem` — a MENSAGEM é do backend, verbatim; o cartão não
  // monta o texto da recusa, mesma disciplina de `escopo_da_verificacao`). As
  // leituras da investigação chegam como `checagens` de id `inv<n>:<leitura>`
  // — o cartão não as trata à parte, elas caem no mesmo `verificadas.map`.
  investigacao?:
    // `id: null` quando a investigação rodou mas não foi gravada
    // (`_gravar_investigacao` falhou): sem continuação, e sem número a mostrar.
    | { id: number | null; encerrada_por: string; leituras: number }
    // `nota_ao_modelo` é instrução ao @Atendente e NUNCA se renderiza aqui.
    | { recusada: 'nao_producao' | 'nao_devops'; mensagem?: string; nota_ao_modelo?: string }
    | null
  // O PARECER do @DBA (`diagnostico: 'consulta_ao_dba'`, spec 2026-10-30-agente-dba §4.7-§4.8).
  // `tipo` é do `classify` do editor no servidor, nunca do modelo: é ele que decide o selo.
  // `sem_where`: UPDATE/DELETE sem WHERE, decidido pelo servidor (§4.8: a tela avisa).
  consulta_sugerida?: {
    sql: string
    tipo: 'leitura' | 'escrita' | 'ddl' | 'desconhecido'
    sem_where?: boolean
  } | null
  // O que o @DBA disse que não leu (texto do parecer). Não é `nao_verificado`, que é a lista
  // de ids de checagem que a métrica do @DevOps conta.
  o_que_nao_foi_verificado?: string[]
  // Onde aplicar a consulta sugerida ("Aplicar no editor", §4.8): identificadores, não segredo.
  alvo_banco?: { tenant_id: number; db_identifier: string; db_name: string | null } | null
}

const SELO_DA_CONSULTA: Record<NonNullable<LaudoDiagnostico['consulta_sugerida']>['tipo'], string> = {
  leitura: 'Leitura',
  escrita: 'Escreve no banco',
  ddl: 'Muda a estrutura do banco',
  desconhecido: 'Tipo não reconhecido',
}

/** Título da checagem: a leitura com rótulo próprio (`banco.colunas`) sai legível; o resto,
 *  que já é título de receita, como veio. */
function tituloDaChecagem(titulo: string): string {
  return FERRAMENTAS_COM_ROTULO.includes(titulo) ? friendlyToolLabel(titulo) : titulo
}

// Rótulo de `encerrada_por` (docstring de `investigacao.conduzir`, mais
// `sem_alvo`/`sem_oferta` de `assistant_tools.py`). Valor NOVO que ainda não
// tem tradução aparece CRU — nunca em branco: um motivo de parada sem rótulo
// ainda é informação, e escondê-lo seria pior que mostrá-lo sem tradução.
const ENCERRADA_POR: Record<string, string> = {
  modelo: 'encerrada',
  teto: 'parou no limite',
  teto_total: 'limite total da investigação',
  trilha_falhou: 'parou: a trilha de auditoria falhou',
  modelo_indisponivel: 'parou: modelo indisponível',
  pedido_invalido: 'encerrada',
  transacao_abortada: 'parou: a transação do banco ficou inválida',
  conteudo_filtrado: 'parou: o provedor do modelo filtrou o conteúdo',
  falhou: 'parou: erro na investigação',
  sem_alvo: 'sem alvo para investigar',
  sem_oferta: 'nenhuma leitura disponível',
}

// Piso para quando o backend não mandar `mensagem` (hoje sempre manda —
// `_RECUSA_DA_INVESTIGACAO` em `assistant_tools.py`). O cartão PREFERE a
// mensagem do backend; isto é só o texto de emergência.
const RECUSA_FALLBACK: Record<string, string> = {
  nao_producao: 'Investigação disponível só fora de produção para o seu perfil',
  nao_devops: 'Investigação ampla exige perfil DevOps. Mostrei o diagnóstico padrão.',
}

// Rótulo de procedência do alvo — casa com `alvo_exibido.resolvido_por` que o
// backend resolve (Task 4). Texto fixo, e não composição: o cartão não decide
// COMO o alvo foi resolvido, só traduz o valor do servidor para o usuário.
const ALVO_RESOLVIDO_POR: Record<NonNullable<LaudoDiagnostico['alvo_exibido']>['resolvido_por'], string> = {
  ancora: 'pelo IP do alerta',
  chamado: 'pelo recurso do chamado',
  texto_do_usuario: 'pelo que você escreveu',
  nome: 'pelo nome',
  consulta_do_editor: 'pela consulta que falhou no editor',
  banco_da_conversa: 'pelo banco aberto no editor',
}

/**
 * "Aplicar no editor" (spec 2026-10-30-agente-dba §4.8): leva a consulta sugerida ao editor SQL
 * da instância e do banco do parecer, no tenant dele. Só põe o texto — quem roda é a pessoa, e o
 * editor aplica o fluxo de escrita quando a consulta escreve. Sem acesso ao tenant, não aparece.
 */
function AplicarNoEditor({ alvo, sql }: {
  alvo: NonNullable<LaudoDiagnostico['alvo_banco']>
  sql: string
}) {
  const navigate = useNavigate()
  const { user, setSelectedTenant } = useAuthStore()
  const deixar = useAplicarNoEditor((s) => s.deixar)
  if (!(user?.tenants ?? []).some((t) => t.id === alvo.tenant_id)) return null
  return (
    <Group>
      <Button size="xs" variant="light" onClick={() => {
        deixar({ tenantId: alvo.tenant_id, instancia: alvo.db_identifier, banco: alvo.db_name, sql })
        setSelectedTenant(alvo.tenant_id)
        navigate('/database?tab=query')
      }}>
        Aplicar no editor
      </Button>
    </Group>
  )
}

// ── Aparência por veredito ─────────────────────────────────────────────────
//
// Cores do design system: `inovareTeal` é a marca e marca o que está certo —
// não um verde genérico. Vermelho e amarelo ficam para falha e suspeita, que é
// o uso semântico que o resto do chat já faz.
const APARENCIA: Record<Veredito, { cor: string; Icone: typeof IconCheck; rotulo: string }> = {
  ok: { cor: 'inovareTeal', Icone: IconCheck, rotulo: 'ok' },
  falha: { cor: 'red', Icone: IconX, rotulo: 'falha' },
  suspeito: { cor: 'yellow', Icone: IconAlertTriangle, rotulo: 'atenção' },
  nao_verificavel: { cor: 'gray', Icone: IconEyeOff, rotulo: 'não verificado' },
}

// ── Discordância ───────────────────────────────────────────────────────────

export interface MarcaDiscordancia {
  motivo: string
  comentario?: string | null
  por_user_id?: number | null
}

// Os três motivos, com o texto que o usuário lê. Cada um aponta para um
// conserto DIFERENTE do nosso lado, e é por isso que a escolha é obrigatória:
// "não bate" sem qual parte não bate não diz onde mexer. Os valores casam com
// `diagnostics.discordancia.MOTIVOS` — o servidor recusa o que não estiver lá.
//
// Não há "outro" de propósito: ele absorveria a maioria dos cliques e a
// contagem morreria sem ninguém perceber. Quem não se encaixa usa o comentário.
const MOTIVOS: { valor: string; rotulo: string; ajuda: string }[] = [
  {
    valor: 'causa_errada',
    rotulo: 'A causa apontada não é a causa',
    ajuda: 'os fatos estão certos, a conclusão não',
  },
  {
    valor: 'fato_errado',
    rotulo: 'Um dos fatos verificados está errado',
    ajuda: 'o que ele diz que leu não é o que está no ambiente',
  },
  {
    valor: 'incompleto',
    rotulo: 'Faltou verificar algo que decide',
    ajuda: 'o que ele leu está certo, mas não alcança o problema',
  },
]

/**
 * KB do `du` em unidade legível — SEMPRE arredondando para BAIXO.
 *
 * `du -k` conta em KiB (base 1024), então a conversão é por 1024 e o rótulo é
 * GiB/MiB, não GB/MB: dizer "GB" sobre um número em base 1024 infla 7% e é a
 * classe de erro que faz a conta na tela não fechar com a do `df`.
 *
 * Para baixo porque o número justifica uma limpeza: arredondar 1,9 GiB para
 * "2 GiB" prometeria espaço que não existe. Abaixo de 1 MiB o número em GiB/MiB
 * viraria "0", que lido ao lado de um disco cheio soa como erro de leitura —
 * então fica em KiB.
 */
export function formatarTamanho(kb: number): string {
  if (!Number.isFinite(kb) || kb < 0) return '—'
  const mib = kb / 1024
  if (mib >= 1024) return `${Math.floor((mib / 1024) * 10) / 10} GiB`
  if (mib >= 1) return `${Math.floor(mib)} MiB`
  return `${Math.floor(kb)} KiB`
}

// Saída de uma leitura da investigação livre (`detalhe_interno.saida`, só
// para quem tem a role que enxerga identificador de infra — o backend já
// stripa o campo para os demais, e o cartão apenas renderiza quando presente,
// nunca deriva; mesma disciplina de `alvo_exibido.detalhe_interno`).
//
// Recolhível e FORA do texto de `observado`: a saída do host pode ser longa
// (até 4000 caracteres, `investigacao.py:_checagem`) e o cartão é o chão de
// fatos — `observado` já diz o essencial redigido; a saída crua é para quem
// quer conferir, não para quem só quer ler o laudo.
function SaidaDaLeitura({ id, saida }: { id: string; saida: string }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div>
      <Button
        variant="subtle"
        color="gray"
        size="compact-xs"
        px={0}
        leftSection={aberto ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
        onClick={() => setAberto((v) => !v)}
        data-saida-toggle={id}
      >
        {aberto ? 'ocultar saída' : 'ver saída'}
      </Button>
      {aberto && (
        <Text
          component="pre"
          size="xs"
          c="dimmed"
          data-saida={id}
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {saida}
        </Text>
      )}
    </div>
  )
}

function Discordancia({
  conversationId,
  messageId,
  marca,
}: {
  conversationId: number
  messageId: number
  marca?: MarcaDiscordancia | null
}) {
  const [aberto, setAberto] = useState(false)
  const [motivo, setMotivo] = useState<string | null>(null)
  const [comentario, setComentario] = useState('')
  const mutation = useDiscordarDoLaudo(conversationId)

  // Já registrada: o cartão mostra a marca em vez do botão. Sem isto o usuário
  // recarrega a página, vê o botão de novo e não tem como saber se registrou.
  if (marca) {
    const rotulo = MOTIVOS.find((m) => m.valor === marca.motivo)?.rotulo ?? marca.motivo
    return (
      <Group gap={6} mt="sm" wrap="nowrap" align="flex-start" data-discordancia="registrada">
        <IconThumbDown size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
        <div>
          <Text size="xs" fw={500} c="dimmed">
            Marcado como “isso não bate” · {rotulo}
          </Text>
          {marca.comentario && (
            <Text size="xs" c="dimmed">
              {marca.comentario}
            </Text>
          )}
        </div>
      </Group>
    )
  }

  return (
    <Popover
      opened={aberto}
      onChange={setAberto}
      position="bottom-start"
      width={320}
      withArrow
      shadow="md"
    >
      <Popover.Target>
        <Button
          variant="subtle"
          color="gray"
          size="compact-xs"
          mt="sm"
          leftSection={<IconThumbDown size={13} />}
          onClick={() => setAberto((v) => !v)}
          data-discordancia="abrir"
        >
          Isso não bate
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            O que não bate? Isso vai para a trilha do diagnóstico — é assim que a
            regra errada chega a quem a conserta.
          </Text>
          <Radio.Group value={motivo} onChange={setMotivo}>
            <Stack gap={6}>
              {MOTIVOS.map((m) => (
                <Radio
                  key={m.valor}
                  value={m.valor}
                  size="xs"
                  label={
                    <div>
                      <Text size="xs">{m.rotulo}</Text>
                      <Text size="xs" c="dimmed">
                        {m.ajuda}
                      </Text>
                    </div>
                  }
                />
              ))}
            </Stack>
          </Radio.Group>
          <Textarea
            size="xs"
            autosize
            minRows={2}
            maxRows={5}
            maxLength={500}
            placeholder="O que você viu (opcional)"
            value={comentario}
            onChange={(e) => setComentario(e.currentTarget.value)}
          />
          <Group justify="flex-start" gap="xs">
            <Button
              size="compact-xs"
              color="inovareTeal"
              disabled={!motivo}
              loading={mutation.isPending}
              onClick={() => {
                if (!motivo) return
                mutation.mutate(
                  { messageId, motivo, comentario: comentario.trim() || undefined },
                  { onSuccess: () => setAberto(false) },
                )
              }}
            >
              Registrar
            </Button>
            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

export function DiagnosticoCard({
  laudo,
  conversationId,
  messageId,
  discordancia,
  regraDeOrdem = null,
}: {
  laudo: LaudoDiagnostico
  /** `metadata.regra_de_ordem` do laudo preparado por regra (spec 2026-09-30 §5.2). */
  regraDeOrdem?: RegraDoLaudo | null
  // Opcionais porque o cartão é renderizado em teste e em Storybook sem
  // conversa em volta. Sem os dois, o controle de discordância simplesmente não
  // aparece — em vez de aparecer e falhar no clique.
  conversationId?: number
  messageId?: number
  discordancia?: MarcaDiscordancia | null
}) {
  const checagens = laudo.checagens ?? []
  const naoVerificadas = checagens.filter((c) => c.veredito === 'nao_verificavel')
  const verificadas = checagens.filter((c) => c.veredito !== 'nao_verificavel')
  const perguntas = laudo.precisa_do_usuario ?? []
  const afirmativo = laudo.status === 'sem_falha_encontrada'
  const textoNaoVerificado = laudo.o_que_nao_foi_verificado ?? []
  const doDba = laudo.diagnostico === 'consulta_ao_dba'

  return (
    <Card withBorder radius="md" p="sm" mt={4} ml={44} maw={620}>
      <Group gap={6} mb={6} wrap="nowrap" align="flex-start">
        <Badge
          size="xs"
          variant="light"
          color={afirmativo ? 'inovareTeal' : laudo.causa_provavel ? 'red' : 'gray'}
        >
          {afirmativo ? 'Está funcionando' : laudo.causa_provavel ? 'Causa provável' : 'Sem conclusão'}
        </Badge>
        {/* PROCEDÊNCIA. O cartão é renderizado DENTRO da conversa do @Atendente,
            e sem este selo o leitor supõe que quem falou foi ele. A distinção
            que o selo preserva é a razão de existir do subsistema: o que está
            neste cartão foi LIDO AO VIVO no ambiente, não escrito por um modelo
            de chat.

            "ao vivo" e não "@DevOps respondeu", de propósito: o nome aqui é
            PROCEDÊNCIA, nunca interlocutor. Um rótulo que soasse como fala
            convidaria o usuário a se dirigir ao @DevOps — e a regra do
            proprietário é que só o time de DevOps faz isso, sempre através do
            @Atendente. */}
        <Badge size="xs" variant="outline" color="inovareTeal">
          {doDba ? '@DBA' : '@DevOps'} · ao vivo
        </Badge>
      </Group>

      {laudo.alvo_exibido && (
        // ALVO, logo abaixo do selo de procedência: antes de ler o veredito, o
        // usuário precisa saber SOBRE QUE MÁQUINA ele fala — sobretudo quando o
        // alvo veio da âncora do alerta e não do que ele escreveu.
        <Text size="xs" c="dimmed" mb={4} data-alvo="1">
          {laudo.alvo_exibido.nome} · {laudo.alvo_exibido.tenant_nome} ·{' '}
          {ALVO_RESOLVIDO_POR[laudo.alvo_exibido.resolvido_por]}
          {laudo.alvo_exibido.detalhe_interno && (
            // Só quem tem a role que enxerga identificador de infra recebe
            // este campo do servidor (D19) — o cartão não filtra, só formata.
            // Monospace por ser dado (instance_id), como `caminho` abaixo.
            <Text
              span
              size="xs"
              c="dimmed"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {' '}
              · {laudo.alvo_exibido.detalhe_interno.instance_id}
              {laudo.alvo_exibido.detalhe_interno.regiao
                ? ` · ${laudo.alvo_exibido.detalhe_interno.regiao}`
                : ''}
            </Text>
          )}
        </Text>
      )}

      {laudo.alvo_exibido?.diverge_do_alerta && (
        // Uma linha, logo abaixo do alvo: o usuário pediu outra máquina, e o
        // cartão diz que ela não é a do alerta — sem contradição nem pergunta
        // do alerta, que o backend não aplica a uma máquina que não é a dele.
        <Text size="xs" c="dimmed" mb={4} data-diverge-do-alerta="1">
          Alvo diferente da máquina do alerta (
          {laudo.alvo_exibido.diverge_do_alerta.maquina_do_alerta})
        </Text>
      )}

      <Text size="sm" fw={laudo.causa_provavel || afirmativo ? 600 : 400} mb="sm">
        {laudo.resumo_para_usuario}
      </Text>

      <Stack gap={4}>
        {verificadas.map((c) => {
          const { cor, Icone, rotulo } = APARENCIA[c.veredito] ?? APARENCIA.nao_verificavel
          const saida = c.detalhe_interno?.saida
          return (
            <Group key={c.id} gap={6} wrap="nowrap" align="flex-start" data-veredito={c.veredito}>
              <Icone size={14} color={`var(--mantine-color-${cor}-6)`} style={{ marginTop: 2 }} />
              <div>
                <Text size="xs" fw={500}>
                  {tituloDaChecagem(c.titulo)}{' '}
                  <Text span size="xs" c="dimmed">
                    · {rotulo}
                  </Text>
                </Text>
                {c.observado && (
                  <Text size="xs" c="dimmed">
                    {c.observado}
                  </Text>
                )}
                {typeof saida === 'string' && saida && (
                  <SaidaDaLeitura id={c.id} saida={saida} />
                )}
              </div>
            </Group>
          )
        })}
      </Stack>

      {laudo.consulta_sugerida && (
        // SUGESTÃO, nunca execução (§4.8): o cartão não tem botão de rodar. O selo sai do
        // `classify` do editor — quem escreve no banco é a pessoa, pelo fluxo de escrita dele.
        <Stack gap={4} mt="sm" data-consulta-sugerida={laudo.consulta_sugerida.tipo}>
          <Group gap={6} wrap="nowrap">
            <Text size="xs" fw={600} c="dimmed">
              Consulta sugerida
            </Text>
            <Badge size="xs" variant="light"
              color={laudo.consulta_sugerida.tipo === 'leitura' ? 'inovareTeal' : 'orange'}>
              {SELO_DA_CONSULTA[laudo.consulta_sugerida.tipo] ?? laudo.consulta_sugerida.tipo}
            </Badge>
          </Group>
          <Code block>{laudo.consulta_sugerida.sql}</Code>
          {laudo.consulta_sugerida.tipo !== 'leitura' && (
            <Text size="xs" c="dimmed">
              Esta sugestão não é só leitura. Confira antes de rodar no editor.
            </Text>
          )}
          {laudo.consulta_sugerida.sem_where && (
            <Text size="xs" c="red" fw={600} data-sem-where>
              Atenção: sem WHERE, esta sugestão altera TODAS as linhas da tabela.
            </Text>
          )}
          {laudo.alvo_banco && (
            <AplicarNoEditor alvo={laudo.alvo_banco} sql={laudo.consulta_sugerida.sql} />
          )}
        </Stack>
      )}

      {laudo.proxima_acao_sugerida && (
        // MEDIÇÃO, não veredito — por isso sem `data-veredito`: um certinho verde
        // ao lado de "o que mais ocupa é /var/lib/docker/volumes" leria como
        // aprovação, que é a leitura errada de um disco cheio.
        //
        // **NÃO tem botão de executar, e isso é desenho.** Quem decide se a ação
        // HABILITA é o `gates.check_all` no servidor (papel, permissão, GMUD,
        // cota, ambiente) e a ação chega ao chat pelo trilho de `proposed_action`,
        // com confirmação própria. Um botão aqui seria um segundo caminho de
        // execução sem esses portões — e o cartão sabe menos que eles: ele só
        // sabe que o verbo está ativo no ambiente, que é uma das cinco
        // condições. Botão que promete e depois recusa é pior que texto.
        <Stack gap={4} mt="sm">
          <Text size="xs" fw={600} c="dimmed">
            O que está ocupando o disco
          </Text>
          <Group gap={6} wrap="nowrap" align="flex-start" data-proxima-acao={laudo.proxima_acao_sugerida.tipo}>
            {laudo.proxima_acao_sugerida.tipo === 'acao_proposta' ? (
              <IconRecycle size={14} color="var(--mantine-color-inovareTeal-6)" style={{ marginTop: 2 }} />
            ) : (
              <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
            )}
            <div>
              <Text size="xs">
                <Text span size="xs" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                  {laudo.proxima_acao_sugerida.caminho}
                </Text>{' '}
                ocupa {formatarTamanho(laudo.proxima_acao_sugerida.kb)} de{' '}
                {formatarTamanho(laudo.proxima_acao_sugerida.total_kb)} medidos
              </Text>
              {laudo.proxima_acao_sugerida.tipo === 'acao_proposta' ? (
                // O NOME vem do catálogo (o backend o lê junto com o estado do
                // verbo). Sem nome, o slug não vai para a tela: `verbo` é
                // identificador nosso, não texto de usuário.
                laudo.proxima_acao_sugerida.nome ? (
                  <Text size="xs" c="dimmed">
                    há uma limpeza liberada para esse lugar: {laudo.proxima_acao_sugerida.nome}
                  </Text>
                ) : (
                  <Text size="xs" c="dimmed">há uma limpeza liberada para esse lugar</Text>
                )
              ) : (
                // O motivo é o produto aqui. Calar transformaria "não posso" em
                // "não sei", e é o que manda o usuário abrir chamado.
                laudo.proxima_acao_sugerida.motivo && (
                  <Text size="xs" c="dimmed">{laudo.proxima_acao_sugerida.motivo}</Text>
                )
              )}
            </div>
          </Group>
        </Stack>
      )}

      {laudo.alternativas && conversationId != null && messageId != null && (
        // A máquina vem do METADATA do laudo (`alvo_exibido`), nunca do texto do
        // bot, que redige o id (§11.15). O id só chega para quem tem a role que
        // enxerga identificador de infraestrutura — sem ele, só o nome.
        <AlternativasDoLaudo
          conversationId={conversationId}
          messageId={messageId}
          alternativas={laudo.alternativas.alternativas ?? []}
          motivoSemAlternativa={laudo.alternativas.motivo_sem_alternativa ?? null}
          maquina={{
            nome: laudo.alvo_exibido?.nome ?? '',
            instanceId: laudo.alvo_exibido?.detalhe_interno?.instance_id ?? null,
          }}
          isProd={false}
          montagem={laudo.montagem_do_alerta?.montagem ?? null}
          regra={regraDeOrdem}
        />
      )}

      {(naoVerificadas.length > 0 || textoNaoVerificado.length > 0) && (
        // Visível, e não atrás de "detalhes": é o que impede o laudo de parecer
        // completo quando não é.
        <Stack gap={4} mt="sm">
          <Text size="xs" fw={600} c="dimmed">
            O que eu não verifiquei
          </Text>
          {naoVerificadas.map((c) => (
            <Group key={c.id} gap={6} wrap="nowrap" align="flex-start" data-veredito={c.veredito}>
              <IconEyeOff size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
              <div>
                <Text size="xs" fw={500}>{c.titulo}</Text>
                {c.motivo_nao_verificavel && (
                  <Text size="xs" c="dimmed">{c.motivo_nao_verificavel}</Text>
                )}
              </div>
            </Group>
          ))}
          {textoNaoVerificado.map((t, i) => (
            <Group key={`texto-${i}`} gap={6} wrap="nowrap" align="flex-start">
              <IconEyeOff size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
              <Text size="xs" c="dimmed">{t}</Text>
            </Group>
          ))}
        </Stack>
      )}

      {laudo.escopo_da_verificacao && (
        // RESSALVA, não checagem — e por isso sem `data-veredito`: um veredito
        // aqui leria como leitura feita, o contrário do que a frase diz.
        //
        // Bloco PRÓPRIO, e não um item de "O que eu não verifiquei": aquela
        // seção existe para leitura que foi tentada e falhou, e só aparece
        // quando há checagem `nao_verificavel`. A ressalva de escopo nasce de
        // pergunta ambígua — a receita pode ter lido tudo o que cobre, com
        // sucesso — então pendurá-la ali a esconderia no caso mais comum.
        <Stack gap={4} mt="sm">
          <Text size="xs" fw={600} c="dimmed">
            O que este laudo não alcança
          </Text>
          <Group gap={6} wrap="nowrap" align="flex-start" data-escopo="1">
            <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
            <Text size="xs" c="dimmed">
              {laudo.escopo_da_verificacao}
            </Text>
          </Group>
        </Stack>
      )}

      {laudo.contradicao_com_alerta && (
        // A âncora do alerta pode divergir do que a leitura encontrou
        // (TASK-11810): o alerta disse X%, e a leitura na máquina resolvida
        // diz outra coisa — ou a montagem nem existe ali. Isto é AVISO sobre o
        // ALVO, não veredito de checagem (por isso cor de warning do design
        // system — `yellow`, a mesma de `suspeito` — e sem `data-veredito`),
        // e fica ANTES da hipótese: quem lê precisa saber que o alvo pode não
        // bater com o alerta antes de ler a leitura do conjunto.
        <Stack gap={4} mt="sm">
          <Group
            gap={6}
            wrap="nowrap"
            align="flex-start"
            data-contradicao="1"
            role="alert"
          >
            <IconAlertTriangle
              size={14}
              color="var(--mantine-color-yellow-6)"
              style={{ marginTop: 2 }}
            />
            <Text size="xs">
              {laudo.contradicao_com_alerta.motivo === 'montagem_ausente'
                ? `O alerta cita ${laudo.contradicao_com_alerta.montagem}, e essa montagem não existe nesta máquina — confira se é a máquina do alerta.`
                : `O alerta diz ${laudo.contradicao_com_alerta.alerta_pct}% em ${laudo.contradicao_com_alerta.montagem}, a leitura diz ${laudo.contradicao_com_alerta.lido_pct}%.`}
            </Text>
          </Group>
        </Stack>
      )}

      {laudo.investigacao && 'recusada' in laudo.investigacao && (
        // O portão (§4.1) barrou ANTES de qualquer leitura — por isso vem
        // como faixa de aviso, não como resultado. A frase é a MENSAGEM do
        // backend (`_RECUSA_DA_INVESTIGACAO`/`_RECUSA_DA_CONTINUACAO`);
        // `RECUSA_FALLBACK` só cobre o caso (hoje inexistente) de ela faltar.
        <Group
          gap={6}
          mt="sm"
          wrap="nowrap"
          align="flex-start"
          data-investigacao-recusada={laudo.investigacao.recusada}
        >
          <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
          <Text size="xs" c="dimmed">
            {laudo.investigacao.mensagem ?? RECUSA_FALLBACK[laudo.investigacao.recusada] ??
              laudo.investigacao.recusada}
          </Text>
        </Group>
      )}

      {laudo.investigacao && 'id' in laudo.investigacao && (
        // Resumo de PROCESSO, não de fato: quantas leituras a investigação
        // livre fez e por que parou. As leituras em si já estão em
        // `checagens` (id `inv<n>:<leitura>`, renderizadas acima).
        <Group gap={6} mt="sm" wrap="nowrap" align="flex-start" data-investigacao="1">
          <IconInfoCircle
            size={14}
            color="var(--mantine-color-inovareTeal-6)"
            style={{ marginTop: 2 }}
          />
          <Text size="xs" c="dimmed">
            Investigação{laudo.investigacao.id != null ? ` #${laudo.investigacao.id}` : ''} ·{' '}
            {laudo.investigacao.leituras} leituras ·{' '}
            {ENCERRADA_POR[laudo.investigacao.encerrada_por] ?? laudo.investigacao.encerrada_por}
          </Text>
        </Group>
      )}

      {laudo.hipotese && (
        // Separado da causa DE PROPÓSITO: o usuário precisa distinguir "isto foi
        // verificado" de "esta é a leitura do conjunto" sem ler
        // documentação. Misturar os dois faria a hipótese herdar a autoridade do
        // fato, que é justamente o que esta fase existe para evitar. Por isso as
        // âncoras ficam à vista: elas dizem SOBRE O QUE a leitura foi feita.
        <Stack gap={4} mt="sm">
          <Text size="xs" fw={600} c="dimmed">
            Leitura do @DevOps sobre o conjunto · confiança {laudo.hipotese.confianca}
          </Text>
          <Group gap={6} wrap="nowrap" align="flex-start" data-hipotese="1">
            <IconBulb size={14} color="var(--mantine-color-inovareTeal-6)" style={{ marginTop: 2 }} />
            <div>
              <Text size="xs">{laudo.hipotese.resumo}</Text>
              <Text size="xs" c="dimmed">
                a partir de: {laudo.hipotese.ancoras.join(', ')}
              </Text>
            </div>
          </Group>
        </Stack>
      )}

      {perguntas.length > 0 && (
        <Stack gap={4} mt="sm">
          {perguntas.map((p) => (
            <Group key={p.campo} gap={6} wrap="nowrap" align="flex-start">
              <IconHelpCircle size={14} color="var(--mantine-color-inovareTeal-6)" style={{ marginTop: 2 }} />
              <Text size="xs">{p.pergunta}</Text>
            </Group>
          ))}
        </Stack>
      )}

      {/* NO FIM do cartão, depois dos fatos: discordar de um laudo que ainda não
          foi lido não é discordância. E discreto (`subtle`, cinza) porque não
          compete com a leitura — é uma saída para quando ela estiver errada. */}
      {conversationId !== undefined && messageId !== undefined && (
        <Discordancia
          conversationId={conversationId}
          messageId={messageId}
          marca={discordancia}
        />
      )}
    </Card>
  )
}
